import { REJECT, nullable, num, object, str, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

import { folderPathSchema, type FolderPath } from 'api/shared/folderPath';

/**
 * A single library as returned by nap-backend `GET /python_libraries/:id`
 * (routes/python_libraries.js). Re-derived from the backend source, NOT from the
 * `pythonLibrariesSlice` `PythonLibrary` type, which described one loose shape for the list row,
 * the single record and both mutation responses at once.
 *
 * This is the bare `<Entity>` — the canonical single-record read the other library shapes are
 * described relative to. It is the ONLY shape carrying `content`: the list `SELECT` omits it,
 * and so do the `INSERT`/`UPDATE … RETURNING` clauses. `content` is `NOT NULL` in the schema
 * (migration 0200) and is what the editor edits, so it is load-bearing rather than optional.
 *
 * The `documentation` column the SELECT also returns is deliberately not carried — see
 * `SavePythonLibrary`.
 */
export interface PythonLibrary {
  readonly python_library_id: number;
  readonly name: string;
  readonly filename: string;
  readonly description: string | null;
  readonly content: string;
  readonly hash: string;
  readonly folder_id: number | null;
  // See getPythonLibraries for why the timestamps are nullable.
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly folder_path: FolderPath | null;
}

const librarySchema = object<PythonLibrary>({
  python_library_id: num(REJECT),
  name: str(),
  filename: str(),
  description: nullable(str()),
  content: str(),
  hash: str(),
  folder_id: nullable(num()),
  created_at: nullable(str()),
  updated_at: nullable(str()),
  folder_path: nullable(folderPathSchema),
});

/**
 * GET `/python_libraries/:id` — one record for the edit dialog, so it never opens on a loosely
 * cast store row (and the list row could not seed it anyway: it has no `content`).
 *
 * Boundary-validated. See `getRecord` for what each outcome means; the short version is that
 * `null` means the server said there is no such record, and the failure arm means it said
 * something we could not read. The caller must tell those apart — a dialog that treats an
 * unreadable answer as "no such record" falls through to create and duplicates the library.
 */
export const getPythonLibrary = (id: number): Promise<Received<PythonLibrary | null>> =>
  getRecord(`/python_libraries/${id}`, { schema: librarySchema, source: 'getPythonLibrary' });
